
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, deleteDoc, doc, query, orderBy, addDoc } from 'firebase/firestore';
import { Edit, Trash2, Plus, User, RefreshCw } from 'lucide-react';
import './AdminPanel.css';

const AuthorManager = () => {
    const [authors, setAuthors] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAuthors();
    }, []);

    const fetchAuthors = async () => {
        setLoading(true);
        try {
            const q = query(collection(db, 'authors'), orderBy('name', 'asc'));
            const querySnapshot = await getDocs(q);
            const authorsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setAuthors(authorsData);
        } catch (error) {
            console.error("Error fetching authors: ", error);
            setAuthors([]);
        } finally {
            setLoading(false);
        }
    };

    const seedAuthors = async () => {
        if (!window.confirm("Add sample authors?")) return;
        setLoading(true);
        try {
            const sampleAuthors = [
                {
                    name: "Sarah Jenkins",
                    role: "Senior Workforce Analyst",
                    bio: "Sarah specializes in global workforce optimization and has over 15 years of experience in the BPO sector.",
                    avatarUrl: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&q=80&w=200",
                    slug: "sarah-jenkins",
                    createdAt: new Date().toISOString()
                },
                {
                    name: "Michael Chen",
                    role: "AI Integration Specialist",
                    bio: "Michael helps businesses implement AI tools into their outsourcing workflows to maximize efficiency.",
                    avatarUrl: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?auto=format&fit=crop&q=80&w=200",
                    slug: "michael-chen",
                    createdAt: new Date().toISOString()
                },
                {
                    name: "Dr. Elena Rodriguez",
                    role: "Organizational Psychologist",
                    bio: "Elena focuses on the human side of outsourcing, ensuring cultural alignment and team well-being.",
                    avatarUrl: "https://images.unsplash.com/photo-1580489944761-15a19d654956?auto=format&fit=crop&q=80&w=200",
                    slug: "dr-elena-rodriguez",
                    createdAt: new Date().toISOString()
                }
            ];

            for (const author of sampleAuthors) {
                await addDoc(collection(db, 'authors'), author);
            }
            alert("Sample authors added!");
            fetchAuthors();
        } catch (error) {
            console.error("Error seeding authors:", error);
            alert("Failed to seed authors.");
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this author?')) {
            try {
                await deleteDoc(doc(db, 'authors', id));
                setAuthors(authors.filter(author => author.id !== id));
            } catch (error) {
                console.error("Error deleting author: ", error);
                alert('Failed to delete author');
            }
        }
    };

    return (
        <div className="panel active">
            <div className="page-hdr">
                <div className="page-hdr-left">
                    <div className="eyebrow">Content</div>
                    <h1>Authors <span>.</span></h1>
                </div>
                <div className="page-hdr-actions">
                    <Link to="/admin/dashboard" className="btn btn-ghost" style={{ borderColor: 'var(--border)', color: 'var(--text2)' }}>
                        Dashboard
                    </Link>
                    <button className="btn btn-ghost" onClick={seedAuthors}>
                        <RefreshCw size={14} /> Seed Authors
                    </button>
                    <Link to="/admin/authors/new" className="btn btn-primary">
                        <Plus size={14} style={{ marginRight: '4px' }} /> New Author
                    </Link>
                </div>
            </div>

            <div className="stats-grid">
                <div className="stat-card">
                    <div className="s-label">Total Authors</div>
                    <div className="s-val">{authors.length}</div>
                </div>
            </div>

            <div className="tbl-wrap">
                {loading ? (
                    <div style={{ padding: '40px', textAlign: 'center', color: 'var(--muted)' }}>Loading authors...</div>
                ) : authors.length === 0 ? (
                    <div style={{ padding: '40px', textAlign: 'center' }}>
                        <p style={{ color: 'var(--muted)', marginBottom: '10px' }}>No authors found.</p>
                        <Link to="/admin/authors/new" style={{ color: 'var(--accent)', fontSize: '13px' }}>Add your first author</Link>
                    </div>
                ) : (
                    <div className="tbl-scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>Author</th>
                                    <th>Role</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {authors.map((author) => (
                                    <tr key={author.id}>
                                        <td>
                                            <div style={{ display: 'flex', items: 'center', gap: '10px' }}>
                                                {author.avatarUrl ? (
                                                    <img src={author.avatarUrl} alt={author.name} style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }} />
                                                ) : (
                                                    <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#eee', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <User size={16} color="#999" />
                                                    </div>
                                                )}
                                                <div style={{ fontWeight: 500 }}>{author.name}</div>
                                            </div>
                                        </td>
                                        <td className="muted">
                                            {author.role || '-'}
                                        </td>
                                        <td>
                                            <div className="action-btns">
                                                <Link to={`/admin/authors/edit/${author.id}`} className="btn-ghost" style={{ padding: '4px', display: 'inline-flex' }}>
                                                    <Edit size={14} />
                                                </Link>
                                                <button onClick={() => handleDelete(author.id)} className="btn-ghost" style={{ padding: '4px', color: 'var(--accent3)', borderColor: 'var(--accent3-dim)' }}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuthorManager;
